/**
 * Wire format between the app and the server for live collaboration.
 *
 * Model: the server keeps, per project, an ordered log of transactions (seq 1, 2, 3…). A client
 * sends its local transactions one at a time; the server applies each to its current state.
 * If it applies, it gets the next seq and is broadcast to every client (the sender included); if
 * not (it touches something another user deleted), it is rejected. Updates carry only the
 * changed fields, so concurrent edits to different properties both survive and the last write
 * wins for the same property. Clients rebase their unconfirmed transactions on every remote one.
 */
import { z } from 'zod';
import { COLLECTIONS, type Project, type Transaction } from '@elec/domain';

const opSchema = z
  .object({
    op: z.enum(['add', 'remove', 'update', 'meta']),
    coll: z.enum(COLLECTIONS).optional(),
    id: z.string().optional(),
    value: z.record(z.string(), z.unknown()).optional(),
    patch: z.record(z.string(), z.unknown()).optional(),
  })
  .refine(
    (o) => (o.op === 'meta' ? !!o.patch : !!o.coll && (o.op === 'add' ? !!o.value : !!o.id)),
    'malformed op',
  );

export const txSchema = z.object({ label: z.string().max(200), ops: z.array(opSchema).min(1).max(50_000) });

export const txRequestSchema = z.object({
  /** Client-chosen id: resending the same transaction (after a disconnect) is harmless. */
  txId: z.string().min(8).max(64),
  clientId: z.string().min(8).max(64),
  /** Seq the client had when it made the change (informative). */
  baseSeq: z.number().int().min(0),
  tx: txSchema,
});
export type TxRequest = { txId: string; clientId: string; baseSeq: number; tx: Transaction };

export type TxResponse =
  | { ok: true; seq: number; duplicate?: boolean }
  | { ok: false; reason: 'conflict' | 'invalid' | 'forbidden'; message: string; headSeq: number };

export interface Presence {
  clientId: string;
  userId: string;
  name: string;
}

/** Events pushed on a project's stream (Server-Sent Events, one JSON per `data:` line). */
export type SyncEvent =
  | {
      type: 'tx';
      seq: number;
      txId: string;
      clientId: string;
      userId: string;
      userName: string;
      at: string;
      tx: Transaction;
    }
  | { type: 'presence'; clients: Presence[] }
  /**
   * Where another client's pointer is on the drawing (sheet units), or `sheetId: null` when it
   * left the canvas. Not stored: only for those connected now.
   */
  | { type: 'cursor'; clientId: string; sheetId: string | null; x: number; y: number }
  | { type: 'reset'; seq: number; project: Project };
